/* Session I/O — save the full Compare workspace to a JSON file, open one back.
 *
 * "Session" = prompt + selected models + responses + synthesis + optimal prompt.
 * Saved as a portable JSON file the user can stash anywhere (Dropbox, git,
 * email attachment, …). Use cases: archiving a comparison for the record,
 * sharing it with a teammate, reproducing it later.
 *
 * UX:
 *   - Save  → showSaveFilePicker (Chrome/Edge) where supported, fallback to
 *             a download blob with a default name `aiboard-YYYY-MM-DD-HHMM.aiboard.json`.
 *   - Open  → showOpenFilePicker (Chrome/Edge) where supported, fallback to
 *             a hidden <input type="file" accept=".json,.aiboard.json">.
 *
 * Schema: `format: "aiboard-session/v1"` so we can evolve later without
 * breaking older files (v2 reader checks the version and dispatches).
 */

import { MODELS } from '../data/models';
import { state, savedState } from './state';
import { renderSelectedPills, updatePickerMeta } from './selected-pills';
import { renderCostLegend } from './cost-legend';
import { toast } from './toast';
import { t, getCurrentLang } from './i18n';

const FORMAT_V1 = 'aiboard-session/v1';
const FORMAT_V2 = 'aiboard-session/v2';
const FORMAT = FORMAT_V2;
const APP_VERSION = '0.2.0';

/* Snapshot schema v2 — the everything-shape.
 *
 * v2 captures the FULL working state: every fan-out response (text +
 * tokens + cost + latency + error), every synthesis call AND every
 * continuation chunk, every export the user produced, the picker UI
 * state, and the backend interaction id. The earlier v1 only kept the
 * latest synthesis text and dropped continuations + deliverables.
 *
 * Back compat: applySnapshot accepts both v1 and v2; v1 files are
 * normalized to v2 in-memory.
 */
export interface SessionSnapshot {
  format: typeof FORMAT_V1 | typeof FORMAT_V2;
  saved_at: string;
  app_version: string;
  lang: string;
  prompt: string;
  selected_models: string[];
  responses: Array<{
    modelId: string;
    text?: string;
    ok?: boolean;
    error?: string;
    durationMs?: number;
    tokensIn?: number;
    tokensOut?: number;
    costUsd?: number;
    provider?: string;
    finishReason?: string;
    truncated?: boolean;
  }>;

  /* v2: array of every synthesis + continuation in order. v1 readers
   * see the LATEST entry under `.synthesis` (still emitted for compat). */
  syntheses?: Array<{
    mode: string;
    model_id: string;
    text: string;
    truncated?: boolean;
    is_continuation?: boolean;
    created_at: string;
  }>;

  /* v1 single-synth field — still written when there's at least one,
   * with the merged latest text. Lets v1 readers (and the cloud history
   * detail page) display SOMETHING even if they don't know v2. */
  synthesis?: {
    mode: string;
    model_id: string;
    text: string;
  };

  /* v2: deliverables the user actually exported in this session. */
  deliverables?: Array<{
    kind: string;
    name: string;
    title?: string;
    created_at: string;
  }>;

  optimal_prompt?: string;

  /* v2: picker / drawer state so restored sessions land with the same
   * mode + model selected for re-running synthesis. */
  ui?: {
    synth_mode?: string;
    synth_model_id?: string;
    synth_custom_instruction?: string;
  };

  /* v2: backend interaction id (returned by /api/history/interaction).
   * If the user resumes work in the loaded session, append-synthesis +
   * append-deliverable still attach to the same row. */
  interaction_id?: string;

  /* v2: prompt attachments — text/word/pptx are inlined as `text`,
   * pdf/images are inlined as base64. Restoring a session puts these
   * back as chips on the prompt area + ready to fire on the next run. */
  attachments?: Array<{
    id: string;
    name: string;
    mime: string;
    size: number;
    kind: 'text' | 'image' | 'pdf' | 'binary';
    text?: string;
    base64?: string;
    sourceMime?: string;
  }>;
}

// ============================================================================
// Snapshot capture (state → JSON object)
// ============================================================================

function getPromptText(): string {
  const ta = document.getElementById('prompt-input') as HTMLTextAreaElement | null;
  return ta?.value ?? state.lastPrompt ?? '';
}

function readDomValue(id: string): string | undefined {
  const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
  if (!el) return undefined;
  const v = (el as any).value;
  return v && String(v).length ? String(v) : undefined;
}

export function captureSnapshot(): SessionSnapshot {
  // Runner stores in-memory results as { modelId, ok, text, inTokens,
  // outTokens, cost, ms, err, provider, status, action, raw, finishReason,
  // truncated }. The snapshot file uses canonical names (tokensIn, etc.)
  // so other tools / future readers don't depend on the runner's internal
  // shape. Translate at the boundary.
  const snap: SessionSnapshot = {
    format: FORMAT_V2,
    saved_at: new Date().toISOString(),
    app_version: APP_VERSION,
    lang: getCurrentLang(),
    prompt: getPromptText(),
    selected_models: [...state.selectedModels],
    responses: (state.lastResponses || []).map((r: any) => ({
      modelId: r.modelId,
      text: r.text,
      ok: r.ok,
      // Accept either canonical or runner-internal field names so we don't
      // lose data if the runner shape evolves and one side gets ahead.
      error:        r.error        ?? r.err          ?? undefined,
      durationMs:   r.durationMs   ?? r.ms           ?? undefined,
      tokensIn:     r.tokensIn     ?? r.inTokens     ?? undefined,
      tokensOut:    r.tokensOut    ?? r.outTokens    ?? undefined,
      costUsd:      r.costUsd      ?? r.cost         ?? undefined,
      provider:     r.provider     ?? undefined,
      finishReason: r.finishReason ?? undefined,
      truncated:    r.truncated    ?? undefined,
    })),
    syntheses: (state.syntheses || []).map(s => ({ ...s })),
    deliverables: (state.deliverables || []).map(d => ({ ...d })),
  };

  // Legacy single-synth field for v1 readers (cloud-blob detail pages,
  // older snapshot readers). Take the LAST entry — for a vanilla single
  // synthesis it's the only one; for a continued one we keep the merged
  // text the way the user sees it on screen.
  if (savedState.lastSynthText) {
    snap.synthesis = {
      mode: savedState.lastSynthMode,
      model_id: savedState.lastSynthModelId,
      text: savedState.lastSynthText,
    };
  }
  if (savedState.lastOptimalPrompt) {
    snap.optimal_prompt = savedState.lastOptimalPrompt;
  }

  // Picker state — survives restore so user can re-run synthesis with
  // the same mode + model.
  const ui = {
    synth_mode:               readDomValue('synth-mode'),
    synth_model_id:           readDomValue('synth-model'),
    synth_custom_instruction: readDomValue('synth-custom-input'),
  };
  if (ui.synth_mode || ui.synth_model_id || ui.synth_custom_instruction) {
    snap.ui = ui;
  }

  const iid = (state as any).lastInteractionId;
  if (iid) snap.interaction_id = iid;

  const atts = (state as any).promptAttachments || [];
  if (atts.length) snap.attachments = atts.map((a: any) => ({ ...a }));

  return snap;
}

// ============================================================================
// Snapshot apply (JSON → state + DOM)
// ============================================================================

export function isValidSnapshot(s: any): s is SessionSnapshot {
  // Accept both v1 and v2.
  return (
    s &&
    typeof s === 'object' &&
    typeof s.format === 'string' &&
    s.format.startsWith('aiboard-session/') &&
    typeof s.prompt === 'string' &&
    Array.isArray(s.selected_models)
  );
}

export function applySnapshot(snap: SessionSnapshot): void {
  // 1. Prompt textarea
  const ta = document.getElementById('prompt-input') as HTMLTextAreaElement | null;
  if (ta) {
    ta.value = snap.prompt;
    // Trigger any input-listeners (char-count, run-button enable, etc.)
    ta.dispatchEvent(new Event('input', { bubbles: true }));
  }
  state.lastPrompt = snap.prompt;

  // 2. Selected models
  state.selectedModels.clear();
  const known = new Set(MODELS.map((m) => m.id));
  snap.selected_models.forEach((id: string) => {
    if (known.has(id)) state.selectedModels.add(id);
  });
  localStorage.setItem('lp_aiboard_selected', JSON.stringify([...state.selectedModels]));
  updatePickerMeta();
  renderSelectedPills();
  renderCostLegend();
  window.dispatchEvent(new CustomEvent('lp-selection-changed'));

  // 3. Responses — dual-write the runner's short names AND canonical names
  //    so cards, exports, and the snapshot all read consistently.
  state.lastResponses = (snap.responses || []).map((r: any) => ({
    modelId: r.modelId,
    ok: r.ok,
    text: r.text,
    provider:     r.provider,
    finishReason: r.finishReason,
    truncated:    !!r.truncated,
    error:      r.error,
    err:        r.error,
    durationMs: r.durationMs,
    ms:         r.durationMs,
    tokensIn:   r.tokensIn,
    inTokens:   r.tokensIn,
    tokensOut:  r.tokensOut,
    outTokens:  r.tokensOut,
    costUsd:    r.costUsd,
    cost:       r.costUsd,
  })) as any;

  // 4. Syntheses array (v2). Back-compat: when a v1 file is loaded it has
  //    only `snap.synthesis`; normalize to a single-entry array.
  let syntheses = Array.isArray(snap.syntheses) && snap.syntheses.length
    ? snap.syntheses.map((s: any) => ({ ...s }))
    : (snap.synthesis
        ? [{
            mode: snap.synthesis.mode || 'merge',
            model_id: snap.synthesis.model_id || '',
            text: snap.synthesis.text || '',
            is_continuation: false,
            created_at: snap.saved_at || new Date().toISOString(),
          }]
        : []);
  state.syntheses = syntheses as any;

  // Latest synth text (with continuations merged) populates savedState so
  // the synth panel renders the same way the user last saw it.
  if (syntheses.length) {
    const head = syntheses[0];
    // Merged text = first synth + every continuation in order. Each
    // continuation only contains its NEW tail (the user prompt told the
    // model to keep going without repeating itself), so a flat join
    // reconstructs what the user last saw on screen.
    const merged = syntheses.map((s: any) => s.text || '').join('');
    savedState.lastSynthText = merged;
    savedState.lastSynthMode = head.mode || '';
    savedState.lastSynthModelId = head.model_id || '';
    savedState.lastSynthTruncated = !!syntheses[syntheses.length - 1].truncated;
  } else {
    savedState.lastSynthText = '';
    savedState.lastSynthMode = '';
    savedState.lastSynthModelId = '';
    savedState.lastSynthTruncated = false;
  }

  // 5. Deliverables (v2 only — silently skip on v1).
  state.deliverables = Array.isArray(snap.deliverables)
    ? snap.deliverables.map((d: any) => ({ ...d }))
    : [];

  savedState.lastOptimalPrompt = snap.optimal_prompt || '';

  // 6. Backend interaction id (v2). Lets resumed work attach to the same row.
  (state as any).lastInteractionId = snap.interaction_id || '';

  // 7. Picker UI state (v2). Set picker values BEFORE dispatching the event
  //    so session-restore's repaint sees the right selection.
  if (snap.ui) {
    if (snap.ui.synth_mode) {
      const el = document.getElementById('synth-mode') as HTMLSelectElement | null;
      if (el) { el.value = snap.ui.synth_mode; el.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    if (snap.ui.synth_model_id) {
      const el = document.getElementById('synth-model') as HTMLSelectElement | null;
      if (el) el.value = snap.ui.synth_model_id;
    }
    if (snap.ui.synth_custom_instruction) {
      const el = document.getElementById('synth-custom-input') as HTMLTextAreaElement | null;
      if (el) el.value = snap.ui.synth_custom_instruction;
    }
  }

  // 8. Prompt attachments (v2) — repopulate the array so chips re-render
  //    and the next run picks them up automatically.
  (state as any).promptAttachments = Array.isArray(snap.attachments)
    ? snap.attachments.map((a: any) => ({ ...a }))
    : [];

  window.dispatchEvent(new CustomEvent('lp-session-loaded', { detail: snap }));
}

// ============================================================================
// File save — modern Save File Picker with download fallback
// ============================================================================

function defaultFilename(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `aiboard-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.aiboard.json`;
}

async function saveViaFilePicker(blob: Blob, filename: string): Promise<boolean> {
  const w = window as any;
  if (typeof w.showSaveFilePicker !== 'function') return false;
  try {
    const handle = await w.showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: 'AI Board session',
          accept: { 'application/json': ['.json', '.aiboard.json'] },
        },
      ],
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch (err: any) {
    // User cancelled — not an error, just bail
    if (err?.name === 'AbortError') return true;
    console.warn('Save File Picker failed, falling back to download', err);
    return false;
  }
}

function saveViaDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

export async function saveSession(): Promise<void> {
  const snap = captureSnapshot();
  if (!snap.prompt && snap.responses.length === 0) {
    toast(t('session.empty') || 'Nothing to save yet — write a prompt or run a comparison first.');
    return;
  }
  const json = JSON.stringify(snap, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const filename = defaultFilename();
  const picked = await saveViaFilePicker(blob, filename);
  if (!picked) saveViaDownload(blob, filename);
  toast(t('session.saved') || `Saved ${filename}`);
}

// ============================================================================
// File open — modern Open File Picker with hidden-input fallback
// ============================================================================

async function readFileViaPicker(): Promise<File | null> {
  const w = window as any;
  if (typeof w.showOpenFilePicker !== 'function') return null;
  try {
    const [handle] = await w.showOpenFilePicker({
      types: [
        {
          description: 'AI Board session',
          accept: { 'application/json': ['.json', '.aiboard.json'] },
        },
      ],
      multiple: false,
    });
    return await handle.getFile();
  } catch (err: any) {
    if (err?.name === 'AbortError') return null;
    console.warn('Open File Picker failed, falling back to <input>', err);
    return null;
  }
}

function readFileViaInput(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.aiboard.json,application/json';
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const file = input.files?.[0] || null;
      document.body.removeChild(input);
      resolve(file);
    });
    input.addEventListener('cancel', () => {
      try { document.body.removeChild(input); } catch {}
      resolve(null);
    });
    document.body.appendChild(input);
    input.click();
  });
}

export async function openSession(): Promise<void> {
  let file = await readFileViaPicker();
  if (!file) file = await readFileViaInput();
  if (!file) return;

  let parsed: any;
  try {
    const text = await file.text();
    parsed = JSON.parse(text);
  } catch (err) {
    toast(t('session.parse_error') || 'Could not parse that file — is it a valid AI Board session JSON?');
    return;
  }
  if (!isValidSnapshot(parsed)) {
    toast(t('session.invalid') || 'That file is not an AI Board session (missing "format" field).');
    return;
  }

  // Optional confirm-replace if there's unsaved work in the current view
  const hasWork =
    (document.getElementById('prompt-input') as HTMLTextAreaElement | null)?.value ||
    state.lastResponses.length > 0;
  if (hasWork) {
    const msg = t('session.replace_confirm')
      || 'Loading this session will replace your current prompt, models, and responses. Continue?';
    if (!window.confirm(msg)) return;
  }

  applySnapshot(parsed);
  toast(t('session.loaded') || 'Session loaded.');
}

// ============================================================================
// Wiring
// ============================================================================

export function attachSessionIoHandlers(): void {
  document.getElementById('session-save-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    saveSession();
  });
  document.getElementById('session-open-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    openSession();
  });
}

if (typeof window !== 'undefined') {
  (window as any).aiBoardSaveSession = saveSession;
  (window as any).aiBoardOpenSession = openSession;
}
